import type { SyncGraph } from "@app/core";
import { getJson, postJson, SyncError, trimBase } from "./http.js";

export { SyncError } from "./http.js";

export interface PullResult {
  /** Server rows changed since the client's cursor (incl. tombstones). */
  changes: SyncGraph;
  /** Opaque high-water mark to send on the next pull. */
  cursor: string;
}

export interface PushResult {
  /** Authoritative versions the server stored — carry server-assigned `rev`
   *  (tasks) and let the client adopt the canonical copy. */
  applied: SyncGraph;
  cursor: string;
}

export interface OnlineSyncKindStats {
  kind: string;
  rows: number;
  active: number;
  deleted: number;
  dataBytes: number;
  keyCount: number;
  maxSeq: number;
  oldestUpdatedAt: string | null;
  newestUpdatedAt: string | null;
}

export interface OnlineSyncStats {
  generatedAt: string;
  cursor: string;
  totalRows: number;
  activeRows: number;
  deletedRows: number;
  dataBytes: number;
  recordsTableBytes: number | null;
  recordsIndexBytes: number | null;
  kinds: OnlineSyncKindStats[];
}

/**
 * The network boundary for multi-device sync. One implementation talks to our
 * own server; a future one could target Google Drive. Pure transport — all
 * convergence logic lives in `@app/core`'s `mergeGraph`.
 */
export interface SyncClient {
  /** Fetch everything changed on the server since `cursor` (null = first sync). */
  pull(cursor: string | null): Promise<PullResult>;
  /** Send local changes; server merges (LWW), assigns `rev`, returns authoritative rows. */
  push(changes: SyncGraph): Promise<PushResult>;
  /** Fetch online storage diagnostics for the signed-in user's synced rows. */
  stats?(): Promise<OnlineSyncStats>;
}

/**
 * HTTP `SyncClient` for our own server. Authenticates with a per-user bearer
 * access token (`getToken`); on a `401` it calls `refresh` once to obtain a new
 * access token and retries the request, so a mid-sync token expiry self-heals.
 */
export class HttpSyncClient implements SyncClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: () => string | null,
    private readonly refresh: () => Promise<string | null>,
  ) {}

  pull(cursor: string | null): Promise<PullResult> {
    return this.authedPost<PullResult>("/sync/pull", { since: cursor });
  }

  push(changes: SyncGraph): Promise<PushResult> {
    return this.authedPost<PushResult>("/sync/push", { changes });
  }

  stats(): Promise<OnlineSyncStats> {
    return this.authedGet<OnlineSyncStats>("/sync/stats");
  }

  private async authedGet<T>(path: string): Promise<T> {
    const url = trimBase(this.baseUrl) + path;
    const token = this.getToken();
    if (!token) throw new SyncError("Not signed in", 401);
    try {
      return await getJson<T>(url, token);
    } catch (e) {
      if (e instanceof SyncError && e.status === 401) {
        const fresh = await this.refresh();
        if (fresh) return getJson<T>(url, fresh);
      }
      throw e;
    }
  }

  private async authedPost<T>(path: string, body: unknown): Promise<T> {
    const url = trimBase(this.baseUrl) + path;
    const token = this.getToken();
    if (!token) throw new SyncError("Not signed in", 401);
    try {
      return await postJson<T>(url, body, { authorization: `Bearer ${token}` });
    } catch (e) {
      if (e instanceof SyncError && e.status === 401) {
        const fresh = await this.refresh();
        if (fresh) return postJson<T>(url, body, { authorization: `Bearer ${fresh}` });
      }
      throw e;
    }
  }
}
